import api from 'api';

// Storify / Raw / Publish and the Raws archive. These call the axios instance
// directly (not getReq/postReq) so the API's error message reaches the editor,
// e.g. "Write and save a headline and a story before publishing".

const unwrap = (res) => res.data.data;

export const newsroomError = (err, fallback) => {
    const message = err?.response?.data?.message;
    if (Array.isArray(message)) return message.join(' ');
    return typeof message === 'string' && message ? message : fallback;
};

export const fetchStory = async (alertId) =>
    unwrap(await api.get(`/alerts/${alertId}/story`));

export const saveStory = async (alertId, story) =>
    unwrap(await api.put(`/alerts/${alertId}/story`, story));

// Not fileUpload: its multipart interceptor stays on after a failed request. Axios sets
// the multipart header and boundary itself for FormData.
const formData = (fields) =>
    Object.entries(fields).reduce((form, [key, value]) => {
        form.append(key, value);
        return form;
    }, new FormData());

// The API checks the file and makes the 1200px copy and the crops; the story must be saved first.
export const uploadLeadImage = async (alertId, file, draft) =>
    unwrap(
        await api.post(
            `/alerts/${alertId}/story/lead-image`,
            formData({
                image: file,
                leadImageAlt: draft.leadImageAlt ?? '',
                leadImageCaption: draft.leadImageCaption ?? '',
                leadImageCredit: draft.leadImageCredit ?? '',
            })
        )
    );

export const uploadSponsorLogo = async (alertId, file) =>
    unwrap(
        await api.post(
            `/alerts/${alertId}/story/sponsor-logo`,
            formData({ file })
        )
    ).url;

// bannerHash is the banner the admin is looking at; the API refuses with 409 if
// the stored one has changed since.
export const confirmSponsorApproval = async (alertId, file, bannerHash) =>
    unwrap(
        await api.post(
            `/alerts/${alertId}/story/sponsor-approval`,
            formData({ file, confirmed: 'true', bannerHash: bannerHash ?? '' })
        )
    );

export const withdrawSponsorApproval = async (alertId) => {
    await api.delete(`/alerts/${alertId}/story/sponsor-approval`);
    return fetchStory(alertId);
};

// The proof is private and the API takes a bearer token, so a plain link would not open it.
export const fetchSponsorProof = async (alertId) =>
    (
        await api.get(`/alerts/${alertId}/story/sponsor-approval/proof`, {
            responseType: 'blob',
        })
    ).data;

export const fetchStorySponsors = async ({
    state = 'all',
    page = 1,
    size = 20,
} = {}) =>
    unwrap(await api.get('/story-sponsors', { params: { state, page, size } }));

export const fetchProfileBySlug = async (slug) =>
    unwrap(await api.get(`/profiles/slug/${encodeURIComponent(slug)}`));

// The story's one offer at publication: { choice: 'faq' } or { choice: 'full', pricePence }.
// Resolves { sent, story, research }; sent is false when the email failed and Send may retry.
export const sendStoryOffer = async (alertId, offer) =>
    unwrap(await api.post(`/alerts/${alertId}/story/offer`, offer));

export const publishStory = async (alertId) =>
    unwrap(await api.post(`/alerts/${alertId}/story/publish`));

export const unpublishStory = async (alertId) =>
    unwrap(await api.post(`/alerts/${alertId}/story/unpublish`));

// Out of Google (noindex,follow, off the sitemaps) or back in; the page stays live either way.
export const setAlertNoindex = async (alertId, noindex) =>
    unwrap(await api.put(`/alerts/${alertId}/noindex`, { noindex }));

export const markRaw = async (alertId, exclusiveUntil = null) =>
    unwrap(await api.post(`/alerts/${alertId}/raw`, { exclusiveUntil }));

// Back to the top of the Raws archive, without touching the alert's own dates.
export const markReraw = async (alertId) =>
    unwrap(await api.post(`/alerts/${alertId}/reraw`));

// "Not a Raw": out of the archive if it was in it, and out of the review queue.
export const markNotRaw = async (alertId) =>
    unwrap(await api.delete(`/alerts/${alertId}/raw`));

export const fetchRaws = async ({ q, tag, page = 1, size = 20 } = {}) => {
    const params = { page, size };
    if (q) params.q = q;
    if (tag) params.tags = tag;
    return unwrap(await api.get('/raws', { params }));
};

export const fetchRawQueue = async ({ page = 1, size = 20 } = {}) =>
    unwrap(await api.get('/raws/queue', { params: { page, size } }));

export const fetchNews = async ({
    page = 1,
    size = 20,
    profile,
    author,
} = {}) =>
    unwrap(await api.get('/news', { params: { page, size, profile, author } }));

export const fetchAuthors = async () => unwrap(await api.get('/authors'));
